import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ENTER } from '@angular/cdk/keycodes';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/legacy-autocomplete';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { User } from 'app/core/models';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Company } from 'app/modules/companies/companies.types';
import { AccountService } from 'app/core';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { CompaniesService } from 'app/modules/companies/companies.service';
import { EngagementsService } from 'app/modules/engagements/engagements.service';
import { Engagement, EngagementType } from '../../engagements.types';
import { Task } from 'app/shared/modules/legacy-tasks/tasks.types';
import moment from 'moment';
import Quill from 'quill';
import { MSWORD_MATCHERS, QUILL_MODULES } from 'app/shared/Utils/quill-editor.utils';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { StripHtmlPipeUtility } from 'app/shared/pipes/strip-html.pipe';

declare interface DialogData {
  contact: Contact;
  company: Company;
  options: any;
  engagement_type: EngagementType;
  engagement: Engagement;
  contacts?: Contact[];
}

@Component({
  selector: 'add-engagement-dialog',
  templateUrl: './add-engagement.component.html',
  styleUrls: ['./add-engagement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEngagementDialogComponent extends OnDestroyMixin implements OnInit {
  engagementForm: UntypedFormGroup;
  quillModules = QUILL_MODULES;
  contactList: Contact[] = [];
  separatorKeysCodes: number[] = [ENTER];
  contactSearchControl = new UntypedFormControl();
  selectedUsers: any[] = [];
  engagementActivityTypes: any[] = [];
  callOutcomes: any[] = [];
  meetingOutcomes: any[] = [];
  callDirections: any[] = ['Inbound', 'Outbound'];
  socialMediaSources: any[] = [];
  createTask = false;
  numberOfDaysToTask = 4;
  contacts: Contact[] = [];
  isLoading: boolean;
  quill: Quill;
  user: User;

  @ViewChild('userInput')
  userInput: ElementRef<HTMLInputElement>;

  @ViewChild('auto')
  matAutocomplete: MatAutocomplete;

  get company(): Company {
    return this.data?.company;
  }

  get contact(): Contact {
    return this.data?.contact;
  }

  get engagement(): Engagement {
    return this.data?.engagement;
  }

  get isEditEngagement(): boolean {
    return this.data?.engagement ? true : false;
  }

  get dynamicDialogTitle(): string {
    return this.isEditEngagement ? 'Edit' : 'Log';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: DialogData,
    private readonly cdRef: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar,
    private readonly untypedFormBuilder: UntypedFormBuilder,
    private readonly matDialogRef: MatDialogRef<AddEngagementDialogComponent>,
    private readonly accountService: AccountService,
    private readonly contactsService: ContactsService,
    private readonly engagementsService: EngagementsService,
    private readonly companyService: CompaniesService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.contactsService.accountId = this.accountService.getCurrentAccount()?.id;
    this.companyService.accountId = this.accountService.getCurrentAccount()?.id;
    this.buildForm();
    this.watchAssociateContactsInputChanges();
    this.user = this.store.selectSnapshot(UserState.getUser);
  }

  private buildForm(): void {
    this.engagementActivityTypes = this.data.options.engagement_activity_types.filter((option) => !option.hidden);
    this.callOutcomes = this.data.options.call_outcomes.filter((option) => !option.hidden);
    this.meetingOutcomes = this.data.options.meeting_outcomes.filter((option) => !option.hidden);
    this.socialMediaSources = this.data.options.social_media_sources.filter((option) => !option.hidden);
    this.contacts = this.data.contacts;

    this.engagementForm = this.untypedFormBuilder.group({
      title: [this.data.engagement?.title ? this.data.engagement.title : ''],
      body: [this.data.engagement?.body ? this.data.engagement.body : '', [Validators.required]],
      call_outcome_id: [''],
      meeting_outcome_id: [''],
      engagement_activity_type_id: [''],
      call_direction: [''],
      engagement_type: [this.data.engagement_type ? this.data.engagement_type : 'Call'],
      social_media_profile: [''],
      social_media_source_id: [''],
      event_date: [moment()],
      createTask: false,
      numberOfDaysToTask: [
        4,
        [Validators.required, Validators.max(999), Validators.min(1), Validators.pattern('[0-9]*')],
      ],
      contact_id: [parseInt(this.contact?.id)],
    });
    this.engagementForm.patchValue(this.data.engagement);

    this.engagementForm
      .get('event_date')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((value) => {
        if (!value) {
          return;
        }

        // Set the 'start' field value from the range
        this.engagementForm.get('event_date').setValue(value, { emitEvent: false });
      });
  }

  private watchAssociateContactsInputChanges(): void {
    this.contactSearchControl.valueChanges
      .pipe(filter(Boolean), untilComponentDestroyed(this), debounceTime(400), distinctUntilChanged())
      .subscribe((query: string) => {
        this.contactsService
          .getContacts(1, 20, 'first_name', 'asc', '', { name: query })
          .pipe(
            untilComponentDestroyed(this),
            map(({ contacts }) => contacts),
          )
          .subscribe((contacts) => {
            this.contactList = contacts;
            this.cdRef.markForCheck();
          });
      });
  }

  /**
   * Get the editor instance and add the MS Word matchers.
   */
  onEditorCreated(quill: Quill): void {
    this.quill = quill;
    const clipboard = this.quill.getModule('clipboard') as any;

    MSWORD_MATCHERS.forEach(([selector, matcher]) => {
      clipboard.addMatcher(selector, matcher);
    });
  }

  isFormDisabled(): boolean {
    return this.isEditEngagement
      ? false
      : this.isLoading || this.engagementForm.pristine || this.engagementForm.invalid;
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedUsers.push({
      id: event.option.id,
      name: event.option.value,
    });
    this.userInput.nativeElement.value = '';
    this.contactSearchControl.reset();
  }

  removeUser(user: User): void {
    this.selectedUsers.splice(
      this.selectedUsers.findIndex((item) => item.id === user.id),
      1,
    );
    this.cdRef.markForCheck();
  }

  resetNumberOfDaysToTask() {
    if (this.engagementForm.get('numberOfDaysToTask').valid) return;
    this.engagementForm.get('numberOfDaysToTask').setValue(1);
  }

  discard(): void {
    this.matDialogRef.close();
  }

  isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  sendAndClose(): void {
    const engagementForm = this.engagementForm.getRawValue();
    const engagement = {
      body: engagementForm.body,
      title: engagementForm.title ? engagementForm.title : this.engagement?.title,
      contact_id: this.contact ? parseInt(this.contact?.id) : engagementForm.contact_id,
      company_id: parseInt(this.company?.id),
      engagement_type: engagementForm.engagement_type,
      call_outcome_id: engagementForm.call_outcome_id,
      meeting_outcome_id: engagementForm.meeting_outcome_id,
      engagement_activity_type_id: engagementForm.engagement_activity_type_id,
      call_direction: engagementForm.call_direction,
      social_media_profile: engagementForm.social_media_profile,
      social_media_source_id: engagementForm.social_media_source_id,
      event_date: engagementForm.event_date,
      id: this.isEditEngagement ? this.engagement.id : null,
    };

    const successMessage = `Successfully ${this.isEditEngagement ? 'updated' : 'created'} engagement.`;
    const errorMessage = `Unable to ${
      this.isEditEngagement ? 'update' : 'create'
    } engagement at the moment. Please try again later.`;

    const request = this.isEditEngagement
      ? this.engagementsService.updateEngagement(engagement)
      : this.engagementsService.createEngagement(engagement);

    const triggerSnackBarOnDialogClose = (message: string, event: 'success' | 'error', engagement?: Engagement) => {
      this.snackBar.open(message, null, {
        duration: 3000,
        panelClass: event === 'success' ? null : 'error-snackbar',
      });
      if (engagementForm.createTask) {
        this.createFollowUpTask(engagementForm, engagement);
      } else {
        this.matDialogRef.close(engagement);
      }
    };

    request.pipe(untilComponentDestroyed(this)).subscribe({
      next: (engagement) => triggerSnackBarOnDialogClose(successMessage, 'success', engagement),
      error: () => triggerSnackBarOnDialogClose(errorMessage, 'error'),
    });
  }

  createFollowUpTask(engagementForm: any, engagement: Engagement) {
    const daysFromNow: number = Number.isInteger(+engagementForm.numberOfDaysToTask)
      ? +engagementForm.numberOfDaysToTask
      : 1;
    let dueDate = new Date(); // Get today's date
    dueDate = new Date(dueDate.getTime() + daysFromNow * 24 * 60 * 60 * 1000); // Add N days in milliseconds

    if (this.contact) {
      const newTask: Task = {
        task_type: 'task',
        task_list_id: this.contact.task_list?.id || this.contact.task_list_id,
        workflow_state: 'not_started',
        title: 'Follow Up - ' + engagementForm.engagement_type,
        due_date: dueDate.toISOString(),
        assigned_user_id: this.user?.id,
        description: StripHtmlPipeUtility.transform(engagementForm.body),
        contact_ids: [this.contact.id],
      };

      this.contactsService
        .createTask(this.contact.id, this.contact.task_list?.id || this.contact.task_list_id, newTask)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => {
          this.matDialogRef.close(engagement);
        });
    } else if (this.company) {
      const newTask: Task = {
        task_type: 'task',
        task_list_id: this.company.task_list?.id || this.company.task_list_id,
        workflow_state: 'not_started',
        title: 'Follow Up - ' + engagementForm.engagement_type,
        due_date: dueDate.toISOString(),
        assigned_user_id: this.user?.id,
        description: StripHtmlPipeUtility.transform(engagementForm.body),
        contact_ids: [engagementForm.contact_id],
      };

      this.companyService
        .createTask(this.company.id, this.company.task_list?.id || this.company.task_list_id, newTask)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => {
          this.matDialogRef.close(engagement);
        });
    }
  }
}
